import { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "../icons/Icon";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../contexts/ToastContext";
import {
  listKiosksFn, createKioskFn, renameKioskFn, repairKioskFn,
} from "../../services/kioskCallables";
import KioskFormModal from "./KioskFormModal";
import PairingCodeModal from "./PairingCodeModal";
import RevokeKioskModal from "./RevokeKioskModal";
import { KIOSK_APP_URL, KIOSK_APP_HOST } from "../../config/kioskApp";
// kiosk-page.css + settings-pages.css carry the modal styling (the modals
// themselves import no CSS). The page below follows DESIGN-STANDARDS.md
// (inline styles), so these imports exist for the modals, not this page.
import "../../styles/components/settings-pages.css";
import "../../styles/components/kiosk-page.css";
import { toYMDString } from "../../utils/periodDate";

const STATUS_LABEL = { paired: "Paired", unpaired: "Awaiting pairing", revoked: "Revoked" };

// Status badge palette per DESIGN-STANDARDS.md §9: paired = green (like Active),
// awaiting = amber (warning), revoked = red (like Inactive).
const STATUS_BADGE = {
  paired: { background: "var(--ui-success-tint)", color: "var(--ui-success-text)" },
  unpaired: { background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)" },
  revoked: { background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)" },
};

// 'YYYY-MM-DD' — the app-wide date standard (DESIGN-STANDARDS.md §13).
// toYMDString reads LOCAL Y/M/D components, never toISOString, so a Manila
// timestamp cannot render the previous day.
// kioskAdmin returns lastSeen/updatedAt as epoch ms, hence the Date wrapper.
const fmtDate = (ms) => (ms ? toYMDString(new Date(ms)) : "");

const KioskGlyph = ({ size = 18, color = "var(--ui-primary)" }) => (
  <Icon name="monitor" />
);

const SortIcon = ({ field, sortField, sortDirection }) => {
  const active = sortField === field;
  if (active && sortDirection === "asc") {
    return <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />;
  }
  if (active && sortDirection === "desc") {
    return <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />;
  }
  return <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />;
};

const StatusBadge = ({ status }) => {
  const c = STATUS_BADGE[status] || STATUS_BADGE.unpaired;
  return (
    <span style={{ background: c.background, color: c.color, padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.75rem", fontWeight: "600", whiteSpace: "nowrap" }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
};

// Small inline action buttons (row + mobile card).
const actionBtn = (variant) => ({
  padding: "0.3125rem 0.75rem",
  border: variant === "danger" ? "1px solid var(--ui-border)" : "1px solid var(--ui-border)",
  background: "white",
  color: variant === "danger" ? "var(--ui-danger-text)" : "var(--ui-text-secondary)",
  borderRadius: "6px",
  fontWeight: "600",
  fontSize: "0.75rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const KioskManagementPage = () => {
  const { hasPermission, company } = useAuth();
  const toast = useToast();
  const canEdit = hasPermission("kiosk:edit");

  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [workingId, setWorkingId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [formModal, setFormModal] = useState(null); // {mode, kiosk?}
  const [codeModal, setCodeModal] = useState(null); // {kioskName, code, expiresAt}
  const [revokeTarget, setRevokeTarget] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await listKiosksFn();
      setKiosks(res.data.kiosks || []);
      setLoadError(false);
    } catch (err) {
      console.error("Failed to load kiosks:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live-refresh the list when a kiosk's pairing state changes elsewhere — e.g. a device
  // pairs on the wall, or is revoked from another session. Kiosk docs are deny-all to
  // clients (C13), so we can't listen to them directly; instead subscribe to the
  // `summaries/kiosks` doc the onKioskWrite trigger bumps on every pair/revoke/create (the
  // same real-time source as the sidebar "awaiting" badge) and re-fetch the callable-backed
  // list when it changes. No polling, no rule relaxation.
  useEffect(() => {
    if (!company?.id) return undefined;
    const ref = doc(db, "companies", company.id, "summaries", "kiosks");
    return onSnapshot(ref, () => { load(); }, (err) => console.error("Kiosk summary listener failed:", err));
  }, [company?.id, load]);

  const stats = useMemo(() => ({
    total: kiosks.length,
    paired: kiosks.filter((k) => k.status === "paired").length,
    awaiting: kiosks.filter((k) => k.status === "unpaired").length,
    revoked: kiosks.filter((k) => k.status === "revoked").length,
  }), [kiosks]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = kiosks.filter((k) => {
      // Status gate. A specific dropdown status always wins. In "All Status" mode with no
      // active search, revoked kiosks are hidden by default (toggle via "Show revoked").
      // An ACTIVE search reveals matches across ALL statuses — so searching a revoked kiosk
      // by name/location/"revoked" still finds it even while the default view hides it.
      if (filterStatus) {
        if (k.status !== filterStatus) return false;
      } else if (!q && !showRevoked && k.status === "revoked") {
        return false;
      }
      if (!q) return true;
      return (k.name || "").toLowerCase().includes(q) ||
        (k.location || "").toLowerCase().includes(q) ||
        (STATUS_LABEL[k.status] || "").toLowerCase().includes(q);
    });
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av; let bv;
      if (sortField === "location") { av = (a.location || "").toLowerCase(); bv = (b.location || "").toLowerCase(); }
      else if (sortField === "status") { av = a.status || ""; bv = b.status || ""; }
      else if (sortField === "updatedAt") { av = a.updatedAt || 0; bv = b.updatedAt || 0; }
      else { av = (a.name || "").toLowerCase(); bv = (b.name || "").toLowerCase(); }
      // localeCompare for strings: a raw < / > compares character codes, so Ñ ranks
      // above every plain letter and Ñuñez sorts last. Numbers keep the raw compare.
      // Matches PayrollSummary / DeductionsStep / GrossIncomeBreakdown, which already do this.
      const c = typeof av === "string" && typeof bv === "string"
        ? av.localeCompare(bv)
        : av < bv ? -1 : av > bv ? 1 : 0;
      return c * dir;
    });
  }, [kiosks, query, filterStatus, showRevoked, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDirection("asc"); }
  };

  const handleCreate = async (name, location) => {
    const res = await createKioskFn({ name, location });
    setFormModal(null);
    setCodeModal({ kioskName: name, code: res.data.code, expiresAt: res.data.expiresAt });
    load();
  };

  const handleRename = async (name, location) => {
    await renameKioskFn({ kioskId: formModal.kiosk.id, name, location });
    setFormModal(null);
    toast.success("Kiosk updated.");
    load();
  };

  const handleRepair = async (kiosk) => {
    if (workingId) return;
    setWorkingId(kiosk.id);
    try {
      const res = await repairKioskFn({ kioskId: kiosk.id });
      setCodeModal({ kioskName: kiosk.name, code: res.data.code, expiresAt: res.data.expiresAt });
      load();
    } catch (err) {
      toast.error(err?.message || "Failed to generate a pairing code.");
    } finally {
      setWorkingId(null);
    }
  };

  const clearFilters = () => { setQuery(""); setFilterStatus(""); setShowRevoked(false); };

  // "Show revoked" toggle switch — only meaningful in All-Status browse; disabled when a
  // specific status is picked (the dropdown already governs revoked visibility then). A real
  // checkbox drives it (visually hidden, on top) so it stays keyboard/screen-reader accessible;
  // the track/knob are decorative (pointer-events: none) so clicks reach the input.
  const revokedToggle = (full) => {
    const disabled = Boolean(filterStatus);
    return (
      <label
        title={disabled ? "Only applies when status is All" : "Show revoked kiosks in the list"}
        style={{
          display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem",
          color: disabled ? "var(--ui-text-faint)" : "var(--ui-text-secondary)", whiteSpace: "nowrap", flexShrink: 0,
          cursor: disabled ? "not-allowed" : "pointer", userSelect: "none",
          ...(full ? { marginBottom: "0.5rem" } : {}),
        }}
      >
        <span style={{ position: "relative", display: "inline-block", width: "34px", height: "20px", flexShrink: 0 }}>
          <input
            type="checkbox"
            checked={showRevoked}
            disabled={disabled}
            onChange={(e) => setShowRevoked(e.target.checked)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", margin: 0, opacity: 0, cursor: disabled ? "not-allowed" : "pointer", zIndex: 1 }}
          />
          <span aria-hidden="true" style={{ position: "absolute", inset: 0, borderRadius: "20px", transition: "background 0.15s", pointerEvents: "none", background: showRevoked ? (disabled ? "var(--ui-text-faint)" : "var(--ui-primary)") : "var(--ui-border)" }} />
          <span aria-hidden="true" style={{ position: "absolute", top: "2px", left: showRevoked ? "16px" : "2px", width: "16px", height: "16px", borderRadius: "50%", background: "white", transition: "left 0.15s", pointerEvents: "none", boxShadow: "0 1px 2px rgba(0,0,0,0.25)" }} />
        </span>
        Show revoked
      </label>
    );
  };

  const rowActions = (k) => {
    if (!canEdit || k.status === "revoked") return null;
    return (
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" style={actionBtn()} onClick={() => setFormModal({ mode: "edit", kiosk: k })}>Edit</button>
        <button
          type="button"
          style={{ ...actionBtn(), opacity: workingId === k.id ? 0.5 : 1, cursor: workingId === k.id ? "wait" : "pointer" }}
          disabled={workingId === k.id}
          onClick={() => handleRepair(k)}
        >
          {k.status === "paired" ? "Re-pair" : "Show code"}
        </button>
        <button type="button" style={actionBtn("danger")} onClick={() => setRevokeTarget(k)}>Revoke</button>
      </div>
    );
  };

  const thStyle = {
    padding: "14px 16px", textAlign: "left", fontWeight: "600", fontSize: "12px",
    textTransform: "uppercase", letterSpacing: "0.5px", color: "white", userSelect: "none", whiteSpace: "nowrap",
  };
  const sortableTh = { ...thStyle, cursor: "pointer" };
  const tdStyle = { padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" };

  const inputStyle = {
    padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: "6px", fontSize: "0.8125rem",
  };

  const addButton = (full) => canEdit && (
    <button
      type="button"
      onClick={() => setFormModal({ mode: "add" })}
      style={{
        padding: "0.5rem 1rem", backgroundColor: "var(--ui-primary)", color: "white", border: "none",
        borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "0.8125rem",
        whiteSpace: "nowrap", flexShrink: 0, width: full ? "100%" : "auto",
      }}
    >
      + Add Kiosk
    </button>
  );

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        {/* Slim title + where-to-pair hint (right-aligned; wraps under on mobile) */}
        <div style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          flexWrap: "wrap", gap: "0.25rem 0.75rem",
          margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
        }}>
          <h1 style={{
            fontSize: isMobile ? "1.125rem" : "1.25rem", fontWeight: "700", color: "var(--ui-primary)", margin: 0,
          }}>
            Kiosks
          </h1>
          <a
            href={KIOSK_APP_URL} target="_blank" rel="noopener noreferrer"
            title="Open the kiosk app in a new tab"
            style={{
              fontSize: "0.8125rem", color: "var(--ui-text-muted)", textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: "0.375rem",
            }}
          >
            <span aria-hidden="true"><Icon name="monitor" size={16} /></span>
            <span>Devices pair at <strong style={{ color: "var(--ui-primary)" }}>{KIOSK_APP_HOST}</strong> ↗</span>
          </a>
        </div>

        {/* Controls */}
        {isMobile ? (
          <div style={{ background: "white", padding: "1rem", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: "0.75rem" }}>
            <input type="search" placeholder="Search kiosks (name, location, status)…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inputStyle, width: "100%", marginBottom: "0.5rem" }} />
            {revokedToggle(true)}
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: "100%", marginBottom: "0.5rem", background: "white" }}>
              <option value="">All Status</option>
              <option value="paired">Paired</option>
              <option value="unpaired">Awaiting pairing</option>
              <option value="revoked">Revoked</option>
            </select>
            {addButton(true)}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.625rem 1rem", background: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: "0.75rem" }}>
            <input type="search" placeholder="Search kiosks (name, location, status)…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: "160px" }} />
            {revokedToggle(false)}
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...inputStyle, minWidth: "140px", background: "white", cursor: "pointer" }}>
              <option value="">All Status</option>
              <option value="paired">Paired</option>
              <option value="unpaired">Awaiting pairing</option>
              <option value="revoked">Revoked</option>
            </select>
            {canEdit && <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />}
            {addButton(false)}
          </div>
        )}

        {loadError && (
          <div style={{ background: "var(--ui-danger-tint)", border: "1px solid var(--ui-danger)", color: "var(--ui-danger-text)", padding: "0.75rem 1rem", borderRadius: "8px", marginBottom: "0.75rem", fontSize: "0.875rem" }}>
            Couldn't load kiosks. Refresh the page to try again.
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <div style={{ width: "48px", height: "48px", border: "3px solid var(--ui-border)", borderTop: "3px solid var(--ui-primary)", borderRadius: "50%", margin: "0 auto 1rem", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "var(--ui-text-muted)" }}>Loading kiosks…</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !loadError && kiosks.length === 0 && (
          <KioskEmptyState canEdit={canEdit} onAdd={() => setFormModal({ mode: "add" })} />
        )}

        {/* No results */}
        {!loading && kiosks.length > 0 && visible.length === 0 && (
          <NoResultsState onClear={clearFilters} />
        )}

        {/* Mobile cards */}
        {!loading && isMobile && visible.length > 0 && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1rem" }}>
              {visible.map((k) => (
                <div key={k.id} style={{ background: "white", borderRadius: "10px", padding: "1rem", border: "2px solid var(--ui-border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                    <div style={{ width: "36px", height: "36px", background: "linear-gradient(135deg, var(--ui-primary-tint) 0%, var(--ui-primary-tint) 100%)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <KioskGlyph />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.9375rem", fontWeight: "600", color: "var(--ui-text)" }}>{k.name}</div>
                    </div>
                    <StatusBadge status={k.status} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--ui-text-muted)", marginBottom: "0.375rem" }}>
                    <span style={{ fontWeight: "500" }}>Location:</span>
                    <span style={{ color: k.location ? "var(--ui-text-secondary)" : "var(--ui-text-faint)" }}>{k.location || "Not set"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--ui-text-muted)", marginBottom: rowActions(k) ? "0.75rem" : 0 }}>
                    <span style={{ fontWeight: "500" }}>Updated:</span>
                    <span style={{ color: k.updatedAt ? "var(--ui-text-secondary)" : "var(--ui-text-faint)" }}>{k.updatedAt ? fmtDate(k.updatedAt) : "—"}</span>
                  </div>
                  {rowActions(k)}
                </div>
              ))}
            </div>
            <KioskFooter stats={stats} />
          </>
        )}

        {/* Desktop table */}
        {!loading && !isMobile && visible.length > 0 && (
          <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflowX: "auto", overflowY: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--ui-gradient-primary)" }}>
                  <th onClick={() => handleSort("name")} style={sortableTh}>Kiosk<SortIcon field="name" sortField={sortField} sortDirection={sortDirection} /></th>
                  <th onClick={() => handleSort("location")} style={sortableTh}>Location<SortIcon field="location" sortField={sortField} sortDirection={sortDirection} /></th>
                  <th onClick={() => handleSort("status")} style={sortableTh}>Status<SortIcon field="status" sortField={sortField} sortDirection={sortDirection} /></th>
                  <th onClick={() => handleSort("updatedAt")} style={sortableTh}>Updated At<SortIcon field="updatedAt" sortField={sortField} sortDirection={sortDirection} /></th>
                  {canEdit && <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((k) => (
                  <tr
                    key={k.id}
                    style={{ borderBottom: "1px solid var(--ui-surface-recessed)", transition: "background-color 0.2s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--ui-primary-tint)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ width: "36px", height: "36px", background: "linear-gradient(135deg, var(--ui-primary-tint) 0%, var(--ui-primary-tint) 100%)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, opacity: k.status === "revoked" ? 0.5 : 1 }}>
                          <KioskGlyph />
                        </div>
                        <span style={{ fontWeight: "500", color: "var(--ui-text)", fontSize: "0.875rem" }}>{k.name}</span>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: k.location ? "var(--ui-text)" : "var(--ui-text-faint)", fontWeight: k.location ? "500" : "400" }}>{k.location || "Not set"}</td>
                    <td style={{ padding: "12px 16px" }}><StatusBadge status={k.status} /></td>
                    <td style={{ ...tdStyle, color: k.updatedAt ? "var(--ui-text)" : "var(--ui-text-faint)", fontWeight: k.updatedAt ? "500" : "400" }}>{k.updatedAt ? fmtDate(k.updatedAt) : "—"}</td>
                    {canEdit && <td style={{ padding: "12px 16px", textAlign: "right" }}><div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>{rowActions(k)}</div></td>}
                  </tr>
                ))}
              </tbody>
            </table>
            <KioskFooter stats={stats} />
          </div>
        )}

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>

      {formModal && (
        <KioskFormModal
          mode={formModal.mode}
          initialName={formModal.kiosk?.name || ""}
          initialLocation={formModal.kiosk?.location || ""}
          onSubmit={formModal.mode === "add" ? handleCreate : handleRename}
          onClose={() => setFormModal(null)}
        />
      )}
      {codeModal && (
        <PairingCodeModal
          kioskName={codeModal.kioskName}
          code={codeModal.code}
          expiresAt={codeModal.expiresAt}
          onClose={() => setCodeModal(null)}
        />
      )}
      {revokeTarget && (
        <RevokeKioskModal
          kiosk={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => { setRevokeTarget(null); load(); }}
        />
      )}
    </div>
  );
};

// Footer — stats + hint, per DESIGN-STANDARDS.md §10.
const KioskFooter = ({ stats }) => (
  <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, var(--ui-bg), var(--ui-surface-recessed))", borderTop: "1px solid var(--ui-border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
    <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
      <FooterStat label="Total Kiosks" value={stats.total} primary />
      <FooterStat label="Paired" value={stats.paired} />
      <FooterStat label="Awaiting" value={stats.awaiting} />
      <FooterStat label="Revoked" value={stats.revoked} />
    </div>
    <span style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ui-text-muted)" }}>
      Pair a device with its one-time code to start accepting punches
    </span>
  </div>
);

const FooterStat = ({ label, value, primary }) => (
  <div>
    <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>{label}</div>
    <div style={{ fontSize: primary ? "20px" : "16px", fontWeight: "600", fontVariantNumeric: "tabular-nums", color: primary ? "var(--ui-primary)" : "var(--ui-text)" }}>{value}</div>
  </div>
);

// Empty state — white card with blue header bar, matching DESIGN-STANDARDS.md.
const KioskEmptyState = ({ canEdit, onAdd }) => (
  <div style={{ background: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid var(--ui-border)", overflow: "hidden" }}>
    <div style={{ backgroundColor: "var(--ui-primary)", padding: "0.875rem 1.5rem" }}>
      <span style={{ color: "white", fontWeight: "600", fontSize: "0.875rem" }}>Kiosks</span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 2rem", textAlign: "center" }}>
      <div style={{ width: "120px", height: "120px", background: "linear-gradient(135deg, var(--ui-primary-tint) 0%, var(--ui-primary-tint) 100%)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.5rem", border: "3px solid var(--ui-primary)" }}>
        <KioskGlyph size={56} />
      </div>
      <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-primary)", margin: "0 0 0.5rem 0" }}>No kiosks yet</h3>
      <p style={{ color: "var(--ui-text-muted)", marginBottom: "2rem", lineHeight: 1.6, maxWidth: "440px", fontSize: "0.9375rem" }}>
        Add a kiosk to pair an on-site device for employee clock in/out. Each kiosk pairs with a
        one-time code that expires after 15 minutes and can be used once. Revoking a kiosk signs out
        its device — its past time records are kept for audit.
      </p>
      {canEdit && (
        <button
          type="button"
          onClick={onAdd}
          style={{ backgroundColor: "var(--ui-primary)", color: "white", padding: "0.875rem 2rem", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "0.9375rem" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--ui-primary-hover)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--ui-primary)"; }}
        >
          + Add Your First Kiosk
        </button>
      )}
    </div>
  </div>
);

// No-results state — matches DESIGN-STANDARDS.md.
const NoResultsState = ({ onClear }) => (
  <div style={{ background: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid var(--ui-border)", overflow: "hidden" }}>
    <div style={{ backgroundColor: "var(--ui-primary)", padding: "0.875rem 1.5rem" }}>
      <span style={{ color: "white", fontWeight: "600", fontSize: "0.875rem" }}>Search Results</span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "3rem 2rem", textAlign: "center" }}>
      <div style={{ width: "80px", height: "80px", background: "var(--ui-surface-recessed)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1rem" }}>
        <Icon name="search" size={36} />
      </div>
      <h3 style={{ fontSize: "1.125rem", fontWeight: "600", color: "var(--ui-text-secondary)", margin: "0 0 0.5rem 0" }}>No kiosks found</h3>
      <p style={{ color: "var(--ui-text-muted)", marginBottom: "1.5rem", fontSize: "0.875rem", maxWidth: "300px" }}>No kiosks match your search or filter. Try adjusting them.</p>
      <button type="button" onClick={onClear} style={{ backgroundColor: "var(--ui-primary)", color: "white", padding: "0.625rem 1.25rem", border: "none", borderRadius: "6px", fontWeight: "600", cursor: "pointer", fontSize: "0.875rem" }}>
        Clear All Filters
      </button>
    </div>
  </div>
);

export default KioskManagementPage;
